import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { STATUS_FLOW } from "../lib/constants";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../lib/auth";
import { Download, Plus, Search } from "lucide-react";

export default function ReturnsListPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/returns", {
        params: { q: q || undefined, status: status === "all" ? undefined : status },
      });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  // Reload whenever the active status filter changes (the search box only triggers
  // a reload on form submit, so we intentionally don't depend on `q` here — see
  // `onSearch` below for that path).
  useEffect(() => { load(); }, [status, load]);
  useEffect(() => { setQ(params.get("q") || ""); }, [params]);

  const onSearch = (e) => {
    e.preventDefault();
    setParams({ q, status: status !== "all" ? status : "" });
    load();
  };

  const exportCsv = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/returns.csv${status !== "all" ? `?status=${encodeURIComponent(status)}` : ""}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="returns-list-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="heading text-3xl font-bold text-slate-900">Return Requests</h1>
          <p className="text-slate-500 mt-1">{rows.length} {rows.length === 1 ? "request" : "requests"}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          {(user.role === "customer" || user.role === "inward" || user.role === "admin") && (
            <Link to="/returns/new">
              <Button className="bg-orange-600 hover:bg-orange-700" data-testid="new-return-button">
                <Plus className="w-4 h-4 mr-2" /> New Request
              </Button>
            </Link>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={onSearch} className="flex gap-2 flex-wrap">
            <div className="flex-1 min-w-[240px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by Request No, Customer, LR, Item..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="returns-search-input"
                className="pl-9"
              />
            </div>
            <Select value={status} onValueChange={(v) => setStatus(v)}>
              <SelectTrigger className="w-56" data-testid="returns-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_FLOW.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="submit" variant="outline" data-testid="returns-search-button">Search</Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Request No</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total Qty</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              if (loading) {
                return (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-8">Loading…</TableCell>
                  </TableRow>
                );
              }
              if (rows.length === 0) {
                return (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-8">No requests found.</TableCell>
                  </TableRow>
                );
              }
              return rows.map((r) => {
              const totalQty = (r.items || []).reduce((a, b) => a + (b.quantity || 0), 0);
              return (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`return-row-${r.request_number}`}>
                  <TableCell className="font-mono font-medium text-orange-700">{r.request_number}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{(r.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell>{r.customer_name}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm">{(r.items || []).map((i) => i.item_name).join(", ")}</TableCell>
                  <TableCell>{totalQty}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right">
                    <Link to={`/returns/${r.id}`} className="text-orange-700 hover:underline text-sm font-medium" data-testid={`view-${r.request_number}`}>
                      View
                    </Link>
                  </TableCell>
                </TableRow>
              );
              });
            })()}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
